import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Receipt, Search, ChevronRight, Sparkles, ArrowUpDown } from "lucide-react";
import GenerateMonthlyInvoicesModal from "@/components/GenerateMonthlyInvoicesModal";
import ViewedChip from "@/components/ViewedChip";

const STATUS_FILTERS = [
    { value: "", label: "All" },
    { value: "draft", label: "Draft" },
    { value: "sent", label: "Sent" },
    { value: "viewed", label: "Viewed" },
    { value: "paid", label: "Paid" },
    { value: "overdue", label: "Overdue" },
    { value: "void", label: "Void" },
];

const STATUS_LABEL = {
    draft: "Draft",
    sent: "Sent",
    viewed: "Viewed",
    paid: "Paid",
    overdue: "Overdue",
    void: "Void",
};

const STATUS_COLOR = {
    draft: "bg-slate-100 text-slate-700",
    sent: "bg-blue-50 text-blue-900",
    viewed: "bg-violet-50 text-violet-800",
    paid: "bg-emerald-100 text-emerald-900",
    overdue: "bg-red-50 text-red-700",
    void: "bg-slate-200 text-slate-700",
};

const SORT_OPTIONS = [
    { value: "-created", label: "Newest first" },
    { value: "created", label: "Oldest first" },
    { value: "-number", label: "Invoice # (Z-A)" },
    { value: "number", label: "Invoice # (A-Z)" },
    { value: "-issue_date", label: "Issue date ↓" },
    { value: "issue_date", label: "Issue date ↑" },
    { value: "-due_date", label: "Due date ↓" },
    { value: "due_date", label: "Due date ↑" },
    { value: "-total", label: "Amount ↓" },
    { value: "total", label: "Amount ↑" },
];

const VIEW_CHIP_STATUSES = new Set(["sent", "viewed", "overdue"]);

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) { if (!s) return "—"; return s.slice(0, 10); }

export default function AdminInvoicesListPage() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    // Phase INV-FILTER-DEFAULT-DRAFT (2026-07-29) — default = "draft"
    // on first visit; user's last pick remembered per-surface via
    // localStorage.
    const LS_KEY = "invoices.list.admin.filter";
    const urlStatus = searchParams.get("status");
    const status = urlStatus ?? (localStorage.getItem(LS_KEY) ?? "draft");
    const sort = searchParams.get("sort") || "-created";
    const fromDate = searchParams.get("from") || "";
    const toDate = searchParams.get("to") || "";
    const [q, setQ] = useState(searchParams.get("q") || "");
    const [genOpen, setGenOpen] = useState(false);

    const updateParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value === "" || value == null) next.delete(key);
        else next.set(key, value);
        // Phase INV-FILTER-DEFAULT-DRAFT — persist status choice.
        if (key === "status") {
            try { localStorage.setItem(LS_KEY, value || ""); } catch { /* noop */ }
        }
        setSearchParams(next, { replace: true });
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = { limit: 100, sort };
            if (status) params.status = status;
            if (q.trim()) params.q = q.trim();
            if (fromDate) params.from = fromDate;
            if (toDate) params.to = toDate;
            const { data } = await api.get("/admin/invoices", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, sort, fromDate, toDate]);

    const onSearch = (e) => {
        e.preventDefault();
        updateParam("q", q.trim());
        load();
    };

    return (
        <DashboardShell>
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900">Billing</p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Invoices</h1>
                    <p className="text-sm text-slate-500 mt-1">Filter, search, sort, and bulk-generate monthly invoices.</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <p className="text-3xl font-display font-bold text-slate-900" data-testid="invoices-total">{total}</p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">Total invoices</p>
                    </div>
                    <Button onClick={() => setGenOpen(true)} className="bg-blue-900 hover:bg-blue-800 gap-2"
                            data-testid="invoices-bulk-generate-btn">
                        <Sparkles size={14} /> Generate monthly
                    </Button>
                </div>
            </div>

            {/* Filter chips */}
            <div className="mt-6 flex flex-wrap gap-2" data-testid="invoices-status-chips">
                {STATUS_FILTERS.map((opt) => {
                    const active = status === opt.value;
                    return (
                        <button
                            key={opt.value || "all"}
                            onClick={() => updateParam("status", opt.value)}
                            data-testid={`invoices-chip-${opt.value || "all"}`}
                            className={
                                "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium transition-colors " +
                                (active
                                    ? "bg-blue-900 text-white"
                                    : "bg-white text-slate-700 ring-1 ring-slate-200 hover:bg-slate-50")
                            }
                        >
                            {opt.label}
                        </button>
                    );
                })}
            </div>

            <Card className="mt-4">
                <CardContent className="p-4 flex flex-wrap gap-3 items-center">
                    <form onSubmit={onSearch} className="flex-1 min-w-[220px] flex gap-2">
                        <div className="relative flex-1">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search invoice # or notes"
                                className="pl-9"
                                data-testid="invoices-search-input"
                            />
                        </div>
                        <Button type="submit" variant="outline" data-testid="invoices-search-go">Search</Button>
                    </form>
                    <div className="flex items-center gap-1.5 text-sm text-slate-500">
                        <span>From</span>
                        <Input type="date" value={fromDate} onChange={(e) => updateParam("from", e.target.value)}
                               className="w-[160px]" data-testid="invoices-from-date" />
                        <span>to</span>
                        <Input type="date" value={toDate} onChange={(e) => updateParam("to", e.target.value)}
                               className="w-[160px]" data-testid="invoices-to-date" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <ArrowUpDown size={14} className="text-slate-400" />
                        <select value={sort} onChange={(e) => updateParam("sort", e.target.value)}
                                className="flex h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                                data-testid="invoices-sort">
                            {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                </CardContent>
            </Card>

            <Card className="mt-4">
                <CardContent className="p-0">
                    {loading && <p className="text-sm text-slate-500 p-6" data-testid="invoices-loading">Loading…</p>}
                    {!loading && items.length === 0 && (
                        <div className="p-12 text-center" data-testid="invoices-empty">
                            <div className="mx-auto h-12 w-12 rounded-full bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400">
                                <Receipt size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">No invoices match</h3>
                            <p className="mt-2 text-sm text-slate-600">Adjust filters above, or click "Generate monthly" to bulk-create for active rentals.</p>
                        </div>
                    )}
                    {!loading && items.length > 0 && (
                        <table className="min-w-full text-sm">
                            <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                                <tr>
                                    <th className="text-left py-3 px-4">Invoice #</th>
                                    <th className="text-left py-3 px-4">Customer</th>
                                    <th className="text-left py-3 px-4">Status</th>
                                    <th className="text-left py-3 px-4">Issue</th>
                                    <th className="text-left py-3 px-4">Due</th>
                                    <th className="text-right py-3 px-4">Amount</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((inv) => (
                                    <tr key={inv.id} data-testid={`invoices-row-${inv.id}`} className="hover:bg-slate-50/50">
                                        <td className="py-3 px-4 font-mono text-slate-800">{inv.invoice_number}</td>
                                        <td className="py-3 px-4">
                                            {inv.customer_name ? (
                                                <Link
                                                    to={`/admin/customers/${inv.customer_id}`}
                                                    className="text-blue-900 hover:underline"
                                                    data-testid={`invoices-customer-${inv.id}`}
                                                >
                                                    {inv.customer_name}
                                                </Link>
                                            ) : (
                                                <span
                                                    className="text-slate-400"
                                                    data-testid={`invoices-customer-${inv.id}`}
                                                    title="Customer was deleted or has no company name on file"
                                                >
                                                    —
                                                </span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[inv.status] || "bg-slate-100"}`}>
                                                    {STATUS_LABEL[inv.status] || inv.status}
                                                </span>
                                                {VIEW_CHIP_STATUSES.has(inv.status) && inv.last_viewed_at && (
                                                    <ViewedChip at={inv.last_viewed_at} by={inv.last_viewed_by_email} />
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-3 px-4 text-slate-600">{fmtDate(inv.issue_date)}</td>
                                        <td className="py-3 px-4 text-slate-600">{fmtDate(inv.due_date)}</td>
                                        <td className="py-3 px-4 text-right font-semibold text-slate-900 tabular-nums">
                                            {fmtMoney(inv.total_cents)}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <Button asChild size="sm" variant="outline" className="gap-1.5">
                                                <Link to={`/admin/invoices/${inv.id}`} data-testid={`invoices-open-${inv.id}`}>
                                                    Open <ChevronRight size={14} />
                                                </Link>
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>

            <GenerateMonthlyInvoicesModal
                open={genOpen}
                onOpenChange={setGenOpen}
                onCreated={() => load()}
            />
        </DashboardShell>
    );
}
